import { Component, inject } from '@angular/core';
import { ApiArticle, ApiUser } from '@tytapp/api';
import { ArticlesService } from '@tytapp/articles';
import { CanonicalURLService, OpenGraph, OpenGraphMetadata, PageComponent, Transfer } from '@tytapp/common';
import { CURRENT_ARTICLE_PROVIDER } from '@tytapp/content';
import { UserService } from '@tytapp/user';
import * as marked from 'marked';

@Component({
    selector: 'tyt-article-page',
    templateUrl: './article.component.html',
    styleUrls: ['./article.component.scss'],
    providers: [
        { provide: CURRENT_ARTICLE_PROVIDER, useExisting: ArticleComponent }
    ]
})
export class ArticleComponent extends PageComponent {
    private userService = inject(UserService);
    private articlesService = inject(ArticlesService);
    private openGraph = inject(OpenGraph);
    private canonicalService = inject(CanonicalURLService);

    @Transfer()
    articleId = null;

    openGraphMetadata: OpenGraphMetadata = {};

    @Transfer()
    state = 'loading';

    get topicIdentifier() {
        if (!this.articleId)
            return null;

        return `article_${this.articleId}`;
    }

    init() {
        this.subscribe(this.userService.userChanged, user => this.user = user);
        this.subscribe(this.route.params, async params => {
            this.articleId = params.id;


                try {
                    this.article = await this.articlesService.get(params.id);
                } catch (e) {
                    if (e.statusCode === 404 || e.error === 'not-found')
                        this.article = null;
                    else
                        throw e;
                }

                if (!this.article) {
                    this.state = 'not-found';
                    return;
                }

                await this.setOpenGraphData();

            const canonicalUrl = await this.articlesService.getArticleUrl(this.article);
            if (this.canonicalService.redirectToCanonical(canonicalUrl))
                return;

            this.url = canonicalUrl;
            this.pageTitle = this.article.title;
            this.head.setCanonicalLink(canonicalUrl);

            this.state = 'ready';
        });
    }

    url: string;

    htmlToPlaintext(text) {
        return text ? String(text).replace(/<[^>]+>/gm, '') : '';
    }

    async setOpenGraphData() {

        if (!this.article)
            return;

        let descriptionHtml;
        let text = '';
        let firstBlockWithText = this.article.blocks.filter(x => x.body)[0];

        if (this.article.feed_summary) {
            text = this.article.feed_summary;
        }

        if (!text && firstBlockWithText) {
            descriptionHtml = marked.marked.parse(firstBlockWithText.body);
            text = this.htmlToPlaintext(descriptionHtml);
            text = text.slice(0, 130) + '...';
        }

        this.openGraphMetadata.title = this.article.title;
        this.openGraphMetadata.type = 'article';
        this.openGraphMetadata.description = text;
        this.openGraphMetadata.locale = 'en_US';
        this.openGraphMetadata.url = await this.articlesService.getArticleUrl(this.article, true);
        this.openGraphMetadata.siteName = 'TYT.com';
        this.openGraphMetadata.image = this.article.lead_image;
        this.openGraphMetadata.articlePublishedTime = new Date(this.article.publish_date).toISOString();

        if (this.openGraphMetadata.image) {
            let delimiter = this.openGraphMetadata.image.includes('?') ? '&' : '?';
            this.openGraphMetadata.image = `${this.openGraphMetadata.image}${delimiter}w=800`;
        }

        this.openGraph.setMetadata(this.openGraphMetadata);
    }

    get pageComponentId() {
        return 'article';
    }

    @Transfer()
    article: ApiArticle;

    @Transfer()
    user: ApiUser;

}
